import { CaretRight, BellRinging } from '@phosphor-icons/react'
import { Tooltip, TooltipContent as TooltipContentShadcn, TooltipTrigger, IconButton } from '@shopflo/ui'
import React, { ReactNode } from 'react'
import { useWidgetConfig } from 'src/components/overview/charts/common/useWidgetConfig'
import { WidgetId, WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'
import { classNames } from 'src/lib/utils/helpers'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'

const SHOPFLO_IMPACT_IMAGE = '/images/misc/shopflo_impact_banner.png'

interface ChartCardProps {
  title: string
  infoTooltip?: string
  children: ReactNode
  className?: string
  loading?: boolean
  minHeight?: number
  maxHeight?: number
  width?: string
  hasBorder?: boolean
  isImpact?: boolean
  redirectLink?: string
  widgetId: string
  threshold_alerts_supported?: boolean
}

export const ChartCardWithWidget = ({
  widgetSection,
  widgetId,
  children,
  minHeight = 460
}: {
  widgetSection: WidgetSections
  widgetId: WidgetId
  children: ReactNode
  minHeight?: number
}) => {
  const {
    tool_tip: infoTooltip,
    widget_title: title,
    impact_config,
    l2_present,
    l2_config,
    threshold_alerts_supported
  } = useWidgetConfig(widgetSection, widgetId)

  return (
    <ChartCard
      title={title}
      infoTooltip={infoTooltip?.primary_text}
      minHeight={minHeight}
      isImpact={impact_config}
      redirectLink={l2_present ? l2_config?.primary : undefined}
      widgetId={widgetId}
      threshold_alerts_supported={threshold_alerts_supported}
    >
      {children}
    </ChartCard>
  )
}

export const TooltipContent = ({ children }: { children: ReactNode }) => {
  return (
    <TooltipContentShadcn side='bottom'>
      <div className=' text-white text-text-xs-medium  rounded-md max-w-60'>{children}</div>
    </TooltipContentShadcn>
  )
}

export const ChartTitle = ({ title, className }: { title: string; className?: string }) => {
  return (
    <h3
      className={classNames(
        `text-sm font-medium text-zinc-600 underline decoration-1 decoration-dotted underline-offset-4 group`,
        className ?? ''
      )}
    >
      <div className='relative'>
        <span className='absolute -top-1 -left-2 w-[calc(100%+16px)] h-[160%] z-1 group-hover:bg-gray-lightest rounded-md transition-colors duration-150'></span>
        <span className='relative z-2'>{title}</span>
      </div>
    </h3>
  )
}
const ChartCard: React.FC<ChartCardProps> = ({
  title,
  infoTooltip,
  children,
  className = '',
  loading = false,
  minHeight = 300,
  maxHeight,
  width = '100%',
  hasBorder = true,
  isImpact = false,
  redirectLink,
  widgetId,
  threshold_alerts_supported = false
}) => {
  const {
    actions: { setAlertDialogState }
  } = useAnalytics()

  return (
    <div
      id={title.replace(' ', '-').toLowerCase()}
      className={classNames(
        'relative bg-white p-4 rounded-xl border-[1px] border-b-[3px] border-solid border-gray-light',
        hasBorder ? 'border border-gray-200' : '',
        className
      )}
      style={{
        width
      }}
    >
      {isImpact && (
        <>
          <div className='absolute top-0 left-0 w-full h-[1px] rounded-t-xl bg-gradient-to-r from-transparent via-fuchsia-500 to-transparent' />
          <div
            className='absolute -top-1 right-3'
            id={widgetId + '-chart-card-impact'}
            data-track-id={widgetId + '-chart-card-impact'}
          >
            <img id='shopflo-impact-image' src={SHOPFLO_IMPACT_IMAGE} alt='Shopflo Impact' className='h-6' />
          </div>

          <div className='absolute bottom-0 left-0 w-full h-[1px] rounded-t-xl bg-gradient-to-r from-transparent via-fuchsia-500 to-transparent' />
        </>
      )}
      {loading ? (
        <div className='animate-pulse'>
          <div className='px-4 pt-4 pb-2 flex justify-between items-start'>
            <div className='flex-1'>
              <div className='h-5 bg-gray-200 rounded-md w-1/3 mb-2'></div>
              <div className='h-3 bg-gray-200 rounded-md w-2/3'></div>
            </div>
            <div className='h-8 w-20 bg-gray-200 rounded-md'></div>
          </div>
          <div className='px-4 pb-4'>
            <div className='bg-gray-200 rounded-md w-full' style={{ height: `${minHeight}px` }}></div>
          </div>
        </div>
      ) : (
        <div className='flex flex-col'>
          <div className='flex justify-between items-start pb-2'>
            <div className='w-full'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <ChartTitle title={title} />
                    </TooltipTrigger>
                    {infoTooltip && <TooltipContent>{infoTooltip}</TooltipContent>}
                  </Tooltip>
                </div>
                <div className={classNames('ml-auto flex items-center gap-1', isImpact ? 'mt-4' : '')}>
                  {threshold_alerts_supported && (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <div onClick={() => setAlertDialogState(true, widgetId)}>
                          <IconButton
                            variant='outline'
                            size='sm'
                            IconComponent={BellRinging}
                            className='text-zinc-500 hover:text-zinc-900 group'
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Set alert</TooltipContent>
                    </Tooltip>
                  )}
                  {redirectLink && (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <a href={redirectLink}>
                          <IconButton
                            variant='outline'
                            size='sm'
                            IconComponent={CaretRight}
                            className='text-zinc-500 hover:text-zinc-900 group'
                          />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>View</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className='flex-1'>
            <div
              className='chart-container'
              style={{
                height: `${minHeight}px`,
                maxHeight: maxHeight ? `${maxHeight}px` : undefined
              }}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChartCard
